import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { Conversation, ConversationId, Message } from '@cyphex/types';
import { ROUTES } from '@/constants/routes';
import { chatApi } from '../api';
import { patchSelfState } from '../lib/conversationCache';

export interface MarkUnreadActions {
  /** "Mark as unread" on a chat-list row — rewinds to its last message. */
  conversation: (conversation: Conversation) => void;
  /** "Mark unread from here" on a bubble. */
  fromMessage: (conversationId: ConversationId, message: Message) => void;
}

/**
 * Marking unread is only useful if it STICKS. The server rewinds the same
 * counters the badge reads and sets `manualUnreadSeq`; the client honours that
 * by leaving the conversation (list rows) or by suppressing auto-mark-read
 * (thread — see `useMarkRead`). Without both halves the badge reappears for a
 * second and then clears itself, which is worse than not having the feature.
 */
export function useMarkUnread(): MarkUnreadActions {
  const client = useQueryClient();
  const navigate = useNavigate();

  const apply = useCallback(
    (conversationId: ConversationId, seq: number | undefined, leaveThread: boolean) => {
      void chatApi
        .markUnread(conversationId, seq === undefined ? {} : { seq })
        .then((state) => {
          patchSelfState(client, conversationId, {
            unreadCount: state.unreadCount,
            lastReadSeq: state.lastReadSeq,
            firstUnreadSeq: state.firstUnreadSeq,
            isManuallyUnread: state.manualUnreadSeq !== null,
          });
          // Staying in the thread would immediately re-read it. Back to the list.
          if (leaveThread) navigate(ROUTES.chat);
        })
        .catch(() => toast.error('Could not mark that unread.'));
    },
    [client, navigate],
  );

  const conversation = useCallback(
    (target: Conversation) => apply(target.id, undefined, false),
    [apply],
  );

  const fromMessage = useCallback(
    (conversationId: ConversationId, message: Message) => apply(conversationId, message.seq, true),
    [apply],
  );

  return { conversation, fromMessage };
}
